import View from "./view.js";
import { esc } from "../escape.js";
import { formatMoney, currencySymbol } from "../currency.js";
import { extractInclusiveTax, taxLabel, defaultTaxLabel, defaultExemptLabel, TAX_LABELS, EXEMPT_LABELS } from "../inclusiveTax.js";

// Receipt Adjustments panel: Tax Settings (+ Tax-Exempt Products summary) and
// Automatic Receipt Adjustments, stacked in one section - no sidebar/tabs and
// no Loyalty placeholder (both removed 2026-07-08). Promo codes moved to their
// own top-level panel (promoView.js) on 2026-07-06.
class DiscountView extends View {
  _panel      = document.querySelector("#discountPanel");
  _adjList    = document.querySelector("#adjPanelList");
  _adjSection = document.querySelector("#adjPanelSection");
  _adjModal   = document.querySelector("#adjPanelModal");
  _adjForm    = document.querySelector("#adjPanelForm");
  _taxSettingsOptions = document.querySelector("#taxSettingsOptions");
  _taxNoneExample = document.querySelector("#taxNoneExample");
  _taxExcludeExample = document.querySelector("#taxExcludeExample");
  _taxIncludeExample = document.querySelector("#taxIncludeExample");
  _taxExclusiveStatusRow = document.querySelector("#taxExclusiveStatusRow");
  _taxInclusiveRateRow = document.querySelector("#taxInclusiveRateRow");
  _taxInclusiveRateInput = document.querySelector("#taxInclusiveRateInput");
  _taxLabelInput = document.querySelector("#taxLabelInput");
  _taxExemptLabelInput = document.querySelector("#taxExemptLabelInput");
  _taxExampleEl = document.querySelector("#taxRateExample");
  _taxFormulaBody = document.querySelector("#taxFormulaBody");
  _taxSettingsFooter = document.querySelector("#taxSettingsFooter");
  _adjustments = []; // cache so the "add a tax adjustment" nudge can react live
  _taxExemptDivider = document.querySelector("#taxExemptDivider");
  _taxExemptSummary = document.querySelector("#taxExemptSummary");
  _taxExemptSummaryCount = document.querySelector("#taxExemptSummaryCount");
  _taxExemptSummarySub = document.querySelector("#taxExemptSummarySub");
  _taxExemptPreview = document.querySelector("#taxExemptPreview");
  _taxExemptManageBtn = document.querySelector("#taxExemptManageBtn");
  _taxExemptModal = document.querySelector("#taxExemptModal");
  _taxExemptModalHint = document.querySelector("#taxExemptModalHint");
  _taxExemptMethods = document.querySelector("#taxExemptMethods");
  _taxExemptList = document.querySelector("#taxExemptList");
  _taxExemptSearch = document.querySelector("#taxExemptSearch");
  _taxExemptSearchResults = document.querySelector("#taxExemptSearchResults");
  _taxExemptThreshold = document.querySelector("#taxExemptThreshold");
  _taxExemptCurrencyPrefix = document.querySelector("#taxExemptCurrencyPrefix");
  _taxExemptCategorySelect = document.querySelector("#taxExemptCategorySelect");
  _taxExemptCount = document.querySelector("#taxExemptCount");
  _taxExemptClearBtn = document.querySelector("#taxExemptClearBtn");
  _taxExemptCancelBtn = document.querySelector("#taxExemptCancelBtn");
  _exemptMenuItems = [];        // full catalogue cache
  _exemptCategories = [];       // cache so Cancel can re-render without a controller round-trip
  _exemptSelectedIds = new Set(); // staged selection - nothing hits the model until Save

  // ── Open / Close ─────────────────────────────────────────────────────────────

  open() {
    this._panel.classList.remove("hidden", "cashflow-exiting");
  }

  close() {
    this._panel.classList.add("cashflow-exiting");
    setTimeout(() => {
      this._panel.classList.add("hidden");
      this._panel.classList.remove("cashflow-exiting");
    }, 220);
  }

  // ── Auto Adjustments ─────────────────────────────────────────────────────────

  renderAdjustments(adjustments) {
    if (!this._adjList) return;
    this._adjustments = adjustments ?? [];
    this._renderTaxAdjustmentStatus();
    if (!adjustments || adjustments.length === 0) {
      this._adjList.innerHTML = `
        <li class="adjustment-empty-state">
          <span class="pos-empty-emoji" aria-hidden="true">🧮</span>
          <p class="pos-empty-title">No adjustments yet</p>
          <p class="pos-empty-sub">Add taxes, service fees, or discounts<br/>that apply to every receipt.</p>
          <button class="btn primary pos-empty-add adj-empty-add" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
            Add Adjustment
          </button>
        </li>`;
      return;
    }
    this._adjList.innerHTML = adjustments
      .map(
        (adj) => `
        <li class="adjustment-item${adj.enabled ? "" : " adjustment-item--off"}" data-id="${adj.id}">
          <div class="adjustment-item-head">
            <div class="adjustment-item-name">${esc(adj.name)}</div>
            <label class="switch">
              <input type="checkbox" class="adj-toggle" ${adj.enabled ? "checked" : ""} />
              <span class="slider round"></span>
            </label>
          </div>
          <div class="adjustment-item-detail">
            <span class="adjustment-item-detail-left">
              <span class="adjustment-type-badge adjustment-type-badge--${adj.type === "fee" ? "fee" : "discount"}">${adj.type === "fee" ? "Fee" : "Discount"}</span>
              ${adj.calculation === "fixed" ? "Fixed amount" : "Percentage"}
            </span>
            <span class="adjustment-item-value adjustment-item-value--${adj.type === "fee" ? "fee" : "discount"}">${adj.type === "fee" ? "+" : "−"}${adj.calculation === "fixed" ? formatMoney(adj.value) : adj.value + "%"}</span>
          </div>
          <p class="adjustment-item-subtext">Applies automatically to every receipt</p>
          <div class="adjustment-item-controls">
            <button class="adjustment-edit-btn adj-panel-edit-btn" data-id="${adj.id}" type="button">Edit</button>
            <div class="adj-more">
              <button class="adj-more-btn" data-action="more" type="button" aria-label="More actions">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
              </button>
              <div class="adj-more-menu hidden">
                <button class="adj-more-item adj-panel-delete-btn" data-id="${adj.id}" type="button">Delete</button>
              </div>
            </div>
          </div>
        </li>
      `
      )
      .join("");
  }

  showAdjustmentForm(adjustment = null) {
    const isEdit = adjustment !== null;
    this._adjForm.innerHTML = `
      <button class="modal-close-btn" id="adjPanelCloseBtn" type="button">&times;</button>
      <h2 class="edit-form-title">${isEdit ? "Edit Adjustment" : "New Adjustment"}</h2>

      <div class="edit-field">
        <label for="adjName">Name</label>
        <input type="text" id="adjName" placeholder="e.g. VAT, Service Charge"
          value="${isEdit ? esc(adjustment.name) : ""}" />
      </div>

      <p class="adj-form-sublabel">Type</p>
      <div class="adj-selector" id="adjTypeSelector">
        <button type="button" class="adj-selector-btn ${!isEdit || adjustment.type === "fee" ? "active" : ""}" data-value="fee">Fee</button>
        <button type="button" class="adj-selector-btn ${isEdit && adjustment.type === "discount" ? "active" : ""}" data-value="discount">Discount</button>
      </div>
      <input type="hidden" id="adjType" value="${isEdit ? adjustment.type : "fee"}" />

      <p class="adj-form-sublabel">Calculation</p>
      <div class="adj-selector" id="adjCalcSelector">
        <button type="button" class="adj-selector-btn ${!isEdit || adjustment.calculation === "fixed" ? "active" : ""}" data-value="fixed">Fixed (${currencySymbol()})</button>
        <button type="button" class="adj-selector-btn ${isEdit && adjustment.calculation === "percentage" ? "active" : ""}" data-value="percentage">
          Percentage (%)
          <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="% of the running subtotal at the time this adjustment is applied">i</span>
        </button>
      </div>
      <input type="hidden" id="adjCalc" value="${isEdit ? adjustment.calculation : "fixed"}" />

      <div class="edit-field">
        <label for="adjValue" id="adjValueLabel">
          ${isEdit && adjustment.calculation === "percentage" ? "Value (%)" : `Value (${currencySymbol()})`}
        </label>
        <input type="number" id="adjValue" min="0" step="0.01" placeholder="0"
          value="${isEdit ? adjustment.value : ""}" />
      </div>

      <div class="adj-form-actions">
        <button type="button" class="btn" id="adjPanelCancelBtn">Cancel</button>
        <button type="button" class="btn primary" id="adjSaveBtn"
          data-edit-id="${isEdit ? adjustment.id : ""}">
          ${isEdit ? "Update" : "Add"}
        </button>
      </div>
    `;

    this._wireAdjForm();
    this._adjModal.classList.remove("hidden");
    document.getElementById("adjName")?.focus();
  }

  _wireAdjForm() {
    // Type selector
    const typeSelector = document.getElementById("adjTypeSelector");
    typeSelector?.addEventListener("click", (e) => {
      const btn = e.target.closest(".adj-selector-btn");
      if (!btn) return;
      typeSelector.querySelectorAll(".adj-selector-btn").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      document.getElementById("adjType").value = btn.dataset.value;
    });

    // Calc selector
    const calcSelector = document.getElementById("adjCalcSelector");
    calcSelector?.addEventListener("click", (e) => {
      const btn = e.target.closest(".adj-selector-btn");
      if (!btn) return;
      calcSelector.querySelectorAll(".adj-selector-btn").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      document.getElementById("adjCalc").value = btn.dataset.value;
      const lbl = document.getElementById("adjValueLabel");
      if (lbl) lbl.textContent = btn.dataset.value === "percentage" ? "Value (%)" : `Value (${currencySymbol()})`;
    });

    document.getElementById("adjPanelCloseBtn")?.addEventListener("click", () => this.closeAdjForm());
    document.getElementById("adjPanelCancelBtn")?.addEventListener("click", () => this.closeAdjForm());
  }

  closeAdjForm() {
    this._adjModal.classList.add("hidden");
    this._adjForm.innerHTML = "";
  }

  _getAdjFormData() {
    return {
      id: document.getElementById("adjSaveBtn")?.dataset.editId || null,
      name: document.getElementById("adjName")?.value.trim(),
      type: document.getElementById("adjType")?.value,
      calculation: document.getElementById("adjCalc")?.value,
      value: parseFloat(document.getElementById("adjValue")?.value),
    };
  }

  // ── Handlers — open / close ───────────────────────────────────────────────────

  _addHandlerOpen(handler) {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-action='discounts']");
      if (!btn) return;
      handler();
    });
  }

  _addHandlerClose(handler) {
    this._panel
      .querySelector(".discount-back")
      .addEventListener("click", () => handler());

    // Close adj form on backdrop click
    this._adjModal.addEventListener("click", (e) => {
      if (e.target === this._adjModal) this.closeAdjForm();
    });
  }

  // ── Handlers — tax mode ───────────────────────────────────────────────────────
  // Three mutually-exclusive option-cards (2026-07-08 redesign, replacing the old
  // toggle switch; "No tax" added same day) - "No tax" / "Tax added at checkout" /
  // "Tax is already included". Only the last maps to taxInclusive=true.

  _setTaxModeSelected(mode) {
    this._taxSettingsOptions?.querySelectorAll(".tax-settings-option").forEach((opt) => {
      opt.classList.toggle("tax-settings-option--selected", opt.dataset.taxMode === mode);
    });
    const inclusive = mode === "inclusive";
    this._taxInclusiveRateRow?.classList.toggle("hidden", !inclusive);
    this._taxExemptDivider?.classList.toggle("hidden", !inclusive);
    this._taxExemptSummary?.classList.toggle("hidden", !inclusive);
    this._taxExclusiveStatusRow?.classList.toggle("hidden", mode !== "exclusive");
  }

  // "Tax added at checkout" alone doesn't charge anything - it's just the
  // label for tax-exclusive pricing. The actual charge still has to come from
  // a matching Fee-type row in Automatic Receipt Adjustments, which is
  // already its own section right below in this same panel - so this nudge
  // just points down to it rather than duplicating its own "Add adjustment"
  // button/form. Content refreshes whenever the adjustments list changes
  // (renderAdjustments), independent of visibility.
  _renderTaxAdjustmentStatus() {
    const el = this._taxExclusiveStatusRow;
    if (!el) return;
    const match = this._adjustments.find(
      (a) => a.type === "fee" && /tax|vat|gst/i.test(a.name || ""),
    );
    el.classList.toggle("tax-exclusive-status--ok", !!match);
    el.classList.toggle("tax-exclusive-status--warn", !match);
    el.innerHTML = match
      ? `<span class="tax-exclusive-status-icon" aria-hidden="true">✓</span>
         <span class="tax-exclusive-status-text">Tax adjustment added: <strong>${esc(match.name)}</strong> (${match.calculation === "fixed" ? formatMoney(match.value) : match.value + "%"})</span>`
      : `<span class="tax-exclusive-status-icon" aria-hidden="true">⚠</span>
         <span class="tax-exclusive-status-text">No tax adjustment added yet. Add one in <button type="button" class="tax-exclusive-status-link" id="taxScrollToAdjustmentsBtn">Automatic Receipt Adjustments</button> below.</span>`;
  }

  // Scrolls to + briefly flashes the Automatic Receipt Adjustments card
  // instead of opening a second, duplicate "add" form - that section already
  // has its own Add adjustment button right there.
  _addHandlerScrollToAdjustments() {
    this._taxExclusiveStatusRow?.addEventListener("click", (e) => {
      if (!e.target.closest("#taxScrollToAdjustmentsBtn")) return;
      const card = document.getElementById("adjOnTopCard");
      if (!card) return;
      card.scrollIntoView({ behavior: "smooth", block: "start" });
      card.classList.add("adj-ontop-card--flash");
      setTimeout(() => card.classList.remove("adj-ontop-card--flash"), 1600);
    });
  }

  _addHandlerSelectTaxMode(handler) {
    this._taxSettingsOptions?.addEventListener("click", (e) => {
      const opt = e.target.closest(".tax-settings-option");
      if (!opt) return;
      const mode = opt.dataset.taxMode;
      if (opt.classList.contains("tax-settings-option--selected")) return;
      this._setTaxModeSelected(mode);
      handler(mode);
    });
  }

  // Mode, rate, and name all feed ONE staged draft now (2026-07-09) - same
  // deferred pattern as the Settings modal's POS/KDS toggles: the change
  // applies locally right away (live example, mode-dependent rows) but the
  // Supabase write waits for the sticky footer's Save Tax Settings. Rate
  // still validates before it's allowed to stage (a bad value shouldn't even
  // look "pending" - the earlier bug this guarded against was auto-SAVING
  // garbage on blur, not staging it, but the range check is worth keeping
  // regardless since nothing else validates it before Save).
  _addHandlerSetTaxRate(handler) {
    if (!this._taxInclusiveRateInput) return;
    this._taxInclusiveRateInput.addEventListener("input", () => {
      this._hideTaxError();
      this._renderTaxExample(this._taxInclusiveRateInput.value);
    });
    this._taxInclusiveRateInput.addEventListener("change", () => {
      const val = parseFloat(this._taxInclusiveRateInput.value);
      if (!Number.isFinite(val) || val <= 0 || val > 50) {
        this._showTaxError("Enter a tax rate between 0.01% and 50%.");
        return;
      }
      this._hideTaxError();
      const rounded = Math.round(val * 100) / 100;
      this._taxInclusiveRateInput.value = rounded;
      handler(rounded);
    });
  }

  _addHandlerSetTaxLabel(handler) {
    if (!this._taxLabelInput) return;
    this._taxLabelInput.addEventListener("change", () => {
      this._renderTaxExample(this._taxInclusiveRateInput?.value);
      handler(this._taxLabelInput.value);
    });
  }

  _addHandlerSetExemptLabel(handler) {
    if (!this._taxExemptLabelInput) return;
    this._taxExemptLabelInput.addEventListener("change", () => {
      handler(this._taxExemptLabelInput.value);
    });
  }

  // Fill both dropdowns from the canonical lists (once). The names are a fixed
  // vocabulary - free text on a legal receipt line was an XSS + compliance risk.
  _populateTaxLabelSelects() {
    if (this._taxLabelInput && !this._taxLabelInput.options.length) {
      this._taxLabelInput.innerHTML = TAX_LABELS
        .map((l) => `<option value="${l}">${l}</option>`)
        .join("");
    }
    if (this._taxExemptLabelInput && !this._taxExemptLabelInput.options.length) {
      this._taxExemptLabelInput.innerHTML = EXEMPT_LABELS
        .map((l) => `<option value="${l}">${l}</option>`)
        .join("");
    }
  }

  showTaxSettingsDirty() {
    this._taxSettingsFooter?.classList.remove("hidden");
  }
  hideTaxSettingsDirty() {
    this._taxSettingsFooter?.classList.add("hidden");
  }

  _addHandlerSaveTaxSettings(handler) {
    document.getElementById("saveTaxSettingsBtn")?.addEventListener("click", () => handler());
  }
  _addHandlerDiscardTaxSettings(handler) {
    document.getElementById("discardTaxSettingsBtn")?.addEventListener("click", () => handler());
  }

  _showTaxError(msg) {
    const el = document.getElementById("taxRateError");
    if (el) { el.textContent = msg; el.classList.remove("hidden"); }
  }
  _hideTaxError() {
    document.getElementById("taxRateError")?.classList.add("hidden");
  }

  // Live worked examples on the option-cards - same rate feeds "added at
  // checkout", so the contrast between add-on-top and stays-the-same is
  // obvious side by side. "No tax" and "already included" both land on
  // "$100 stays $100" arithmetically, so their trailing clause is what
  // actually distinguishes them (no tax line at all vs. broken out on the
  // receipt) - otherwise they'd read as identical.
  _renderTaxModeExamples(rate) {
    const r = parseFloat(rate);
    if (this._taxNoneExample) {
      this._taxNoneExample.textContent = `Example: ${formatMoney(100)} stays ${formatMoney(100)} - no tax line on the receipt.`;
    }
    if (this._taxExcludeExample) {
      this._taxExcludeExample.textContent = Number.isFinite(r) && r > 0
        ? `Example: ${formatMoney(100)} + ${formatMoney(100 * (r / 100))} tax = ${formatMoney(100 + 100 * (r / 100))}`
        : "";
    }
    if (this._taxIncludeExample) {
      this._taxIncludeExample.textContent = `Example: ${formatMoney(100)} stays ${formatMoney(100)} - tax is broken out on the receipt.`;
    }
  }

  // Breakdown card (what the customer pays, front and center) + a compact
  // formula behind the disclosure. Deliberately does NOT frame "add X% on
  // top" as wrong - that's the separate Automatic Receipt Adjustments feature
  // (service charges, fees, discounts, or tax added ON TOP of the total), so
  // the closing line points there instead of arguing against it.
  _renderTaxExample(rate) {
    this._renderTaxModeExamples(rate);
    const r = parseFloat(rate);
    const vat = extractInclusiveTax(100, r);
    // Prefer the dropdown's current pick (live), else the saved/country label.
    const lbl = this._taxLabelInput?.value || taxLabel();

    if (this._taxExampleEl) {
      this._taxExampleEl.innerHTML = vat
        ? `
        <p class="tax-rate-example-lead">Your listed price already includes ${esc(lbl)}, so PointBunny <strong>does not add ${esc(lbl)} on top</strong>. Instead, it works out the ${esc(lbl)} portion already inside the price and shows it separately on the receipt - the normal way to calculate tax that's already included in a price.</p>
        <div class="tax-rate-example-breakdown">
          <p class="tax-rate-example-heading">${formatMoney(100)} price with ${r}% included ${esc(lbl)}</p>
          <div class="tax-rate-example-row"><span>Price before tax</span><span>${formatMoney(vat.net)}</span></div>
          <div class="tax-rate-example-row"><span>Included ${esc(lbl)}</span><span>${formatMoney(vat.tax)}</span></div>
          <div class="tax-rate-example-row tax-rate-example-row--total"><span>Customer pays</span><span>${formatMoney(100)}</span></div>
        </div>`
        : "";
    }

    if (this._taxFormulaBody) {
      if (!vat) { this._taxFormulaBody.innerHTML = ""; }
      else {
        const onTop = 100 * (r / 100); // what an on-top Adjustment at the same rate would add instead
        this._taxFormulaBody.innerHTML = `
          <div class="tax-formula-eq">
            <div class="tax-formula-line"><span class="tax-formula-k">${esc(lbl)}</span><span>= Price × Rate ÷ (100 + Rate)</span></div>
            <div class="tax-formula-line"><span class="tax-formula-k"></span><span>= ${formatMoney(100)} × ${r} ÷ ${100 + r} = <strong>${formatMoney(vat.tax)}</strong></span></div>
          </div>
          <p class="tax-formula-note">Need to charge something on top of the sale instead? Add an automatic receipt adjustment below. For example, adding ${r}% on top of a ${formatMoney(100)} sale would make the total ${formatMoney(100 + onTop)}.</p>`;
      }
    }
  }

  syncTaxMode(mode, rate, label, exLabel) {
    if (!this._taxSettingsOptions) return;
    this._taxInclusiveRateInput.value = rate ?? 12;
    this._setTaxModeSelected(mode);
    this._populateTaxLabelSelects();
    if (this._taxLabelInput) {
      // Stored pick if it's canonical, else the country default (a legacy
      // free-text value from when this was a text input falls back too).
      this._taxLabelInput.value = TAX_LABELS.includes(label) ? label : defaultTaxLabel();
    }
    if (this._taxExemptLabelInput) {
      this._taxExemptLabelInput.value = EXEMPT_LABELS.includes(exLabel) ? exLabel : defaultExemptLabel();
    }
    this._renderTaxExample(rate ?? 12);
  }

  // ── Tax-Exempt Products (centralized exemption picker) ──────────────────────
  // Product selection here is the ONLY place tax_class is edited now - the Edit
  // Item modal shows a read-only label + a deep link into this panel.
  //
  // Model: a "build a list" picker, not a giant checkbox wall. The right column
  // is the staged selection (chips, removable); the left column offers three
  // ADDITIVE ways to add to it (search-and-pick one at a time, by price, by
  // category). Nothing hits the DB until Save - Cancel reverts to last-saved.

  renderExemptProducts(menuItems = [], categories = []) {
    if (!this._taxExemptList) return;
    this._exemptMenuItems = menuItems;
    this._exemptCategories = categories;
    this._exemptSelectedIds = new Set(
      menuItems.filter((i) => i.taxClass === "exempt").map((i) => i._id),
    );
    if (this._taxExemptCurrencyPrefix) this._taxExemptCurrencyPrefix.textContent = currencySymbol();
    if (this._taxExemptModalHint) this._taxExemptModalHint.textContent = `Choose products that should not have ${taxLabel()} extracted from their listed price.`;
    if (this._taxExemptSummarySub) this._taxExemptSummarySub.textContent = `${taxLabel()} will not be extracted from the listed price of these products.`;

    if (this._taxExemptCategorySelect) {
      this._taxExemptCategorySelect.innerHTML =
        `<option value="" disabled selected>Select category</option>` +
        categories
          .map((c) => `<option value="${esc(c)}">${esc(c.charAt(0).toUpperCase() + c.slice(1))}</option>`)
          .join("");
    }

    this._collapseExemptMethods();
    if (this._taxExemptSearch) this._taxExemptSearch.value = "";
    this._renderExemptSearchResults("");
    this._renderExemptSelectedList();
    this._renderExemptSummary();
  }

  // The compact summary card (outside the modal) - always reflects SAVED
  // truth (item.taxClass), never the modal's in-progress staged selection,
  // so it can't flicker to show an unsaved pending state.
  _renderExemptSummary() {
    const exempt = this._exemptMenuItems.filter((i) => i.taxClass === "exempt");
    if (this._taxExemptSummaryCount) this._taxExemptSummaryCount.textContent = `${exempt.length} selected`;
    if (this._taxExemptPreview) {
      if (!exempt.length) {
        this._taxExemptPreview.textContent = "No products marked exempt yet.";
      } else {
        const shown = exempt.slice(0, 3).map((i) => i.itemName);
        const extra = exempt.length - shown.length;
        this._taxExemptPreview.textContent = shown.join(" · ") + (extra > 0 ? ` · +${extra} more` : "");
      }
    }
  }

  _renderExemptSelectedList() {
    if (!this._taxExemptList) return;
    const items = this._exemptMenuItems.filter((i) => this._exemptSelectedIds.has(i._id));
    if (!items.length) {
      this._taxExemptList.innerHTML = `
        <li class="tax-exempt-empty-state">
          <p class="pos-empty-sub">No exempt products yet.<br/>Add some from the left.</p>
        </li>`;
    } else {
      this._taxExemptList.innerHTML = items
        .map(
          (item) => `
        <li class="tax-exempt-chip" data-id="${item._id}">
          <span class="tax-exempt-chip-text">
            <span class="tax-exempt-chip-name">${esc(item.itemName)}</span>
            <span class="tax-exempt-chip-meta">${esc(item.category || "Uncategorized")} · ${formatMoney(item.price)}</span>
          </span>
          <button type="button" class="tax-exempt-chip-remove" aria-label="Remove ${esc(item.itemName)}">&times;</button>
        </li>`,
        )
        .join("");
    }
    if (this._taxExemptCount) this._taxExemptCount.textContent = `${items.length} selected`;
    this._taxExemptClearBtn?.classList.toggle("hidden", !items.length);
  }

  // Search results exclude anything already staged - it's already visible (and
  // removable) in the right column, so showing it again here would be a
  // confusing duplicate "add" affordance.
  _renderExemptSearchResults(query) {
    if (!this._taxExemptSearchResults) return;
    const q = query.trim().toLowerCase();
    if (!q) {
      this._taxExemptSearchResults.innerHTML = `<li class="tax-exempt-search-hint">Type to search products…</li>`;
      return;
    }
    const matches = this._exemptMenuItems
      .filter((i) => !this._exemptSelectedIds.has(i._id) && i.itemName.toLowerCase().includes(q))
      .slice(0, 20);
    if (!matches.length) {
      this._taxExemptSearchResults.innerHTML = `<li class="tax-exempt-search-hint">No matching products.</li>`;
      return;
    }
    this._taxExemptSearchResults.innerHTML = matches
      .map(
        (item) => `
      <li class="tax-exempt-search-row" data-id="${item._id}">
        <span class="tax-exempt-chip-name">${esc(item.itemName)}</span>
        <span class="tax-exempt-chip-meta">${esc(item.category || "Uncategorized")} · ${formatMoney(item.price)}</span>
      </li>`,
      )
      .join("");
  }

  // Only one Add-method panel is expanded at a time; the other two dim (never
  // fully hide, so their icon/label stay legible as a hint of what's available).
  _collapseExemptMethods() {
    this._taxExemptMethods?.querySelectorAll(".tax-exempt-method").forEach((m) => {
      m.classList.remove("tax-exempt-method--active", "tax-exempt-method--dim");
      m.querySelector(".tax-exempt-method-body")?.classList.add("hidden");
    });
  }

  _openExemptMethod(method) {
    if (!method) return;
    this._collapseExemptMethods();
    this._taxExemptMethods?.querySelectorAll(".tax-exempt-method").forEach((m) => {
      if (m !== method) m.classList.add("tax-exempt-method--dim");
    });
    method.classList.add("tax-exempt-method--active");
    method.querySelector(".tax-exempt-method-body")?.classList.remove("hidden");
  }

  // ── Modal (2026-07-08: the picker now lives in a modal, opened from the
  // Tax Settings card's summary; the summary is what stays inline) ──────────

  openExemptModal() {
    this._taxExemptModal?.classList.remove("hidden");
  }

  closeExemptModal() {
    this._taxExemptModal?.classList.add("hidden");
  }

  _addHandlerOpenExemptModal() {
    this._taxExemptManageBtn?.addEventListener("click", () => this.openExemptModal());
  }

  // × button and Cancel both discard any unsaved staging and close.
  _addHandlerCloseExemptModal() {
    const revertAndClose = () => {
      this.renderExemptProducts(this._exemptMenuItems, this._exemptCategories);
      this.closeExemptModal();
    };
    document.getElementById("taxExemptModalCloseBtn")?.addEventListener("click", revertAndClose);
    this._taxExemptCancelBtn?.addEventListener("click", revertAndClose);
  }

  // Scrolls to + briefly flashes a specific product's row - used when arriving
  // via the Edit Item modal's "Manage in Receipt Adjustments" link. If the item isn't
  // exempt yet, opens the search method pre-filled with its name instead, so
  // the owner can add it in one click.
  focusExemptProduct(itemId) {
    const item = this._exemptMenuItems.find((i) => i._id === itemId);
    if (!item) return;
    this.openExemptModal();

    if (this._exemptSelectedIds.has(itemId)) {
      requestAnimationFrame(() => {
        const chip = this._taxExemptList?.querySelector(`.tax-exempt-chip[data-id="${itemId}"]`);
        if (!chip) return;
        chip.scrollIntoView({ behavior: "smooth", block: "center" });
        chip.classList.add("tax-exempt-chip--flash");
        setTimeout(() => chip.classList.remove("tax-exempt-chip--flash"), 1600);
      });
      return;
    }

    const searchMethod = this._taxExemptMethods?.querySelector('.tax-exempt-method[data-method="search"]');
    this._openExemptMethod(searchMethod);
    if (this._taxExemptSearch) {
      this._taxExemptSearch.value = item.itemName;
      this._taxExemptSearch.focus();
    }
    this._renderExemptSearchResults(item.itemName);
    searchMethod?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  _addHandlerExemptMethodToggle() {
    this._taxExemptMethods?.addEventListener("click", (e) => {
      const head = e.target.closest(".tax-exempt-method-head");
      if (!head) return;
      const method = head.closest(".tax-exempt-method");
      const wasActive = method.classList.contains("tax-exempt-method--active");
      this._collapseExemptMethods();
      if (wasActive) return; // clicking the open one again just closes everything
      this._openExemptMethod(method);
      method.querySelector("input, select")?.focus();
    });
  }

  _addHandlerExemptSearch() {
    this._taxExemptSearch?.addEventListener("input", () => {
      this._renderExemptSearchResults(this._taxExemptSearch.value);
    });
  }

  _addHandlerExemptSearchPick() {
    this._taxExemptSearchResults?.addEventListener("click", (e) => {
      const row = e.target.closest(".tax-exempt-search-row");
      if (!row) return;
      this._exemptSelectedIds.add(row.dataset.id);
      this._renderExemptSelectedList();
      this._renderExemptSearchResults(this._taxExemptSearch?.value ?? "");
    });
  }

  // Both bulk helpers below are ADDITIVE (add more candidates, never remove) -
  // they're suggestions the owner reviews in the right column before Save.
  _addHandlerExemptAutoSelect() {
    document.getElementById("taxExemptAutoBtn")?.addEventListener("click", () => {
      const threshold = parseFloat(this._taxExemptThreshold?.value);
      if (!Number.isFinite(threshold) || threshold <= 0) {
        this._taxExemptThreshold?.focus();
        return;
      }
      this._exemptMenuItems.forEach((i) => {
        if (i.price < threshold) this._exemptSelectedIds.add(i._id);
      });
      this._renderExemptSelectedList();
      this._renderExemptSearchResults(this._taxExemptSearch?.value ?? "");
    });
  }

  _addHandlerExemptCategorySelect() {
    document.getElementById("taxExemptCategoryBtn")?.addEventListener("click", () => {
      const category = this._taxExemptCategorySelect?.value;
      if (!category) return;
      this._exemptMenuItems.forEach((i) => {
        if (i.category === category) this._exemptSelectedIds.add(i._id);
      });
      this._renderExemptSelectedList();
      this._renderExemptSearchResults(this._taxExemptSearch?.value ?? "");
    });
  }

  _addHandlerExemptRemove() {
    this._taxExemptList?.addEventListener("click", (e) => {
      const btn = e.target.closest(".tax-exempt-chip-remove");
      if (!btn) return;
      const id = btn.closest(".tax-exempt-chip")?.dataset.id;
      if (!id) return;
      this._exemptSelectedIds.delete(id);
      this._renderExemptSelectedList();
      this._renderExemptSearchResults(this._taxExemptSearch?.value ?? "");
    });
  }

  _addHandlerExemptClear() {
    this._taxExemptClearBtn?.addEventListener("click", () => {
      if (!this._exemptSelectedIds.size) return;
      if (!window.confirm("Remove all products from the tax-exempt list? This won't save until you click Save changes.")) return;
      this._exemptSelectedIds.clear();
      this._renderExemptSelectedList();
      this._renderExemptSearchResults(this._taxExemptSearch?.value ?? "");
    });
  }

  _addHandlerExemptSave(handler) {
    document.getElementById("taxExemptSaveBtn")?.addEventListener("click", () => {
      handler([...this._exemptSelectedIds]);
      this.closeExemptModal();
    });
  }

  // ── Handlers — auto adjustments ───────────────────────────────────────────────

  _addHandlerAdjAdd() {
    this._panel
      .querySelector("#adjPanelAddBtn")
      ?.addEventListener("click", () => this.showAdjustmentForm());
    // Delegated: the centered empty-state Add button (re-rendered on each render)
    this._adjList?.addEventListener("click", (e) => {
      if (e.target.closest(".adj-empty-add")) this.showAdjustmentForm();
    });
  }

  _addHandlerAdjSave(handler) {
    this._adjModal.addEventListener("click", (e) => {
      if (!e.target.closest("#adjSaveBtn")) return;
      const data = this._getAdjFormData();
      if (!data.name) { alert("Please enter a name."); return; }
      if (isNaN(data.value) || data.value < 0) { alert("Please enter a valid value."); return; }
      handler(data);
      this.closeAdjForm();
    });
  }

  _addHandlerAdjEdit(handler) {
    this._adjList.addEventListener("click", (e) => {
      const btn = e.target.closest(".adj-panel-edit-btn");
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }

  _addHandlerAdjDelete(handler) {
    this._adjList.addEventListener("click", (e) => {
      const btn = e.target.closest(".adj-panel-delete-btn");
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }

  _addHandlerAdjToggle(handler) {
    this._adjList.addEventListener("change", (e) => {
      const toggle = e.target.closest(".adj-toggle");
      if (!toggle) return;
      const id = toggle.closest(".adjustment-item").dataset.id;
      handler(id);
    });
  }

  // Kebab "more" menu (Delete lives here now) - mirrors staffView's .staff-more
  // pattern: opening one closes any other open menu; clicking outside closes it.
  _addHandlerAdjMoreToggle() {
    this._adjList.addEventListener("click", (e) => {
      const btn = e.target.closest(".adj-more-btn");
      if (!btn) return;
      const menu = btn.closest(".adj-more")?.querySelector(".adj-more-menu");
      this._adjList.querySelectorAll(".adj-more-menu").forEach((m) => { if (m !== menu) m.classList.add("hidden"); });
      menu?.classList.toggle("hidden");
    });
    document.addEventListener("click", (e) => {
      if (e.target.closest(".adj-more")) return;
      this._adjList?.querySelectorAll(".adj-more-menu").forEach((m) => m.classList.add("hidden"));
    });
  }
}

export default new DiscountView();
